/* =========================================================
   Novatto — Globo GF/SPF Search UI Fixes (Dawn 15.4)
   Scope: Search page only

   Fixes:
   ✅ Forms styling (white bg + deep purple text)
   ✅ Sort dropdown text centering
   ✅ Real caret injected + animated for:
      - Sort dropdown ("Relevance")
      - Filter headers (COLLECTION / PRICE / etc.)
   ✅ Survives Globo re-rendering (MutationObserver + timed retries)
   ========================================================= */

(function () {
  const INK = "#041049";
  const BG = "#ffffff";
  const BORDER = "rgba(4,16,73,0.22)";
  const SOFT = "rgba(4,16,73,0.08)";

  function isSearchTemplate() {
    const html = document.documentElement;
    const body = document.body;
    return (
      html?.classList.contains("template-search") ||
      body?.classList.contains("template-search") ||
      location.pathname === "/search"
    );
  }

  function setInline(el, styles) {
    if (!el || el.nodeType !== 1) return;
    for (const [k, v] of Object.entries(styles)) {
      el.style.setProperty(k, v, "important");
    }
  }

  /* -----------------------------
     CARET HELPERS (DOM, not ::after)
  ----------------------------- */

  function ensureCaret(el, caretClass) {
    let caret = el.querySelector(`.${caretClass}`);
    if (!caret) {
      caret = document.createElement("span");
      caret.className = caretClass;
      caret.setAttribute("aria-hidden", "true");
      el.appendChild(caret);
    }
    return caret;
  }

  function setCaretState(caretEl, isOpen) {
    // Down (closed) vs Up (open)
    caretEl.style.setProperty(
      "transform",
      isOpen
        ? "translateY(-35%) rotate(-135deg)" // ▲ open
        : "translateY(-55%) rotate(45deg)",  // ▼ closed
      "important"
    );
  }

  function styleCaret(caretEl) {
    setInline(caretEl, {
      "position": "absolute",
      "right": "12px",
      "top": "50%",
      "width": "8px",
      "height": "8px",
      "border-right": `2px solid ${INK}`,
      "border-bottom": `2px solid ${INK}`,
      "pointer-events": "none",
      "z-index": "9",
      "opacity": "1",
      "transition": "transform 140ms ease",
    });
  }

  /* -----------------------------
     APPLY FIXES
  ----------------------------- */

  function applyFixes() {
    if (!isSearchTemplate()) return;

    // Only proceed if Globo GF/SPF appears present
    const gfRoot =
      document.querySelector(".gf-theme-version-15.spf-has-filter") ||
      document.querySelector("#gf-tree") ||
      document.querySelector("[class*='gf-']") ||
      document.querySelector("[class*='spf-']");

    if (!gfRoot) return;

    // Debug marker (optional)
    document.documentElement.dataset.novGloboFix = "on";

    /* ============================
       A) FORMS (Show / Sort / inputs)
       ============================ */

    // "Show" limit select
    document
      .querySelectorAll("#setLimit.sortby-select, .gf-filter-selection select.sortby-select")
      .forEach((el) => {
        setInline(el, {
          "background-color": BG,
          "color": INK,
          "border": `1px solid ${BORDER}`,
          "box-shadow": "none",
        });
      });

    // Custom Sort toggle ("Relevance")
    document.querySelectorAll(".sort-by .sort-by-toggle").forEach((toggle) => {
      // Make it look like a centered control
      setInline(toggle, {
        "background-color": BG,
        "color": INK,
        "border": `1px solid ${BORDER}`,
        "display": "inline-flex",
        "align-items": "center",
        "justify-content": "center",
        "text-align": "center",
        "line-height": "1",
        "min-height": "40px",
        "padding": "10px 36px 10px 14px", // right padding reserves caret space
        "position": "relative",
        "overflow": "visible",
      });

      // Ensure inner text centers reliably
      const labelSpan = toggle.querySelector("span");
      if (labelSpan) {
        setInline(labelSpan, {
          "color": INK,
          "display": "inline-block",
          "width": "100%",
          "text-align": "center",
        });
      }

      // Inject real caret (sort toggle)
      const caret = ensureCaret(toggle, "nov-sort-caret");
      styleCaret(caret);

      // Set caret state based on aria-expanded
      const expanded = toggle.getAttribute("aria-expanded") === "true";
      setCaretState(caret, expanded);

      // Bind interactions once (so it animates when opened/closed)
      if (!toggle.dataset.novCaretBound) {
        toggle.dataset.novCaretBound = "1";

        const updateCaret = () => {
          const isOpen = toggle.getAttribute("aria-expanded") === "true";
          setCaretState(caret, isOpen);
        };

        toggle.addEventListener("click", () => {
          // Globo may toggle aria-expanded after its own handler runs
          setTimeout(updateCaret, 0);
          setTimeout(updateCaret, 50);
        });

        toggle.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
            setTimeout(updateCaret, 0);
            setTimeout(updateCaret, 50);
          }
        });

        // Initial sync
        setTimeout(updateCaret, 0);
      }
    });

    // Sort dropdown menu container
    document
      .querySelectorAll("#globo-dropdown-sort_options.globo-dropdown-custom__options")
      .forEach((menu) => {
        setInline(menu, {
          "background-color": BG,
          "border": `1px solid ${BORDER}`,
        });

        menu.querySelectorAll(":scope > span").forEach((opt) => {
          setInline(opt, { "color": INK });
          opt.onmouseenter = () => setInline(opt, { "background-color": SOFT });
          opt.onmouseleave = () => setInline(opt, { "background-color": "transparent" });
        });
      });

    // Filter-search input
    document.querySelectorAll("input.gf-controls-search-input").forEach((el) => {
      setInline(el, {
        "background-color": BG,
        "color": INK,
        "-webkit-text-fill-color": INK,
        "border": `1px solid ${BORDER}`,
        "box-shadow": "none",
      });
    });

    // Range inputs (min/max)
    document.querySelectorAll(".gf-range-inputs input").forEach((el) => {
      setInline(el, {
        "background-color": BG,
        "color": INK,
        "-webkit-text-fill-color": INK,
        "border": `1px solid ${BORDER}`,
        "box-shadow": "none",
      });
    });

    // noUi slider styling (+ spacing so it doesn’t kiss the next header)
    document.querySelectorAll(".gf-range-slider.noUi-target").forEach((el) => {
      setInline(el, {
        "background-color": "rgba(4,16,73,0.10)",
        "border": `1px solid ${BORDER}`,
        "margin-bottom": "14px",
      });
    });

    document.querySelectorAll(".gf-range-slider .noUi-connect").forEach((el) => {
      setInline(el, { "background-color": INK });
    });

    document.querySelectorAll(".gf-range-slider .noUi-handle").forEach((el) => {
      setInline(el, {
        "background-color": BG,
        "border": `2px solid ${INK}`,
        "box-shadow": "none",
      });
    });

    document.querySelectorAll(".gf-range-slider .noUi-value").forEach((el) => {
      setInline(el, { "color": INK });
    });

    // Labels in the controls row
    document.querySelectorAll(".gf-filter-selection label").forEach((el) => {
      setInline(el, { "color": INK });
    });

    /* ============================
       B) FILTER HEADER CARETS (COLLECTION / PRICE / etc.)
       ============================ */

    // Globo headers are typically inside: #gf-tree .gf-block-title h3 (or .h3)
    document
      .querySelectorAll("#gf-tree .gf-block-title h3, #gf-tree .gf-block-title .h3")
      .forEach((h) => {
        // Ensure header can host caret
        setInline(h, {
          "position": "relative",
          "padding-right": "28px",
          "overflow": "visible",
          "cursor": "pointer",
        });

        // Inject caret
        const caret = ensureCaret(h, "nov-filter-caret");
        // Place a bit closer than sort caret (headers are narrower)
        setInline(caret, {
          "position": "absolute",
          "right": "4px",
          "top": "50%",
          "width": "8px",
          "height": "8px",
          "border-right": `2px solid ${INK}`,
          "border-bottom": `2px solid ${INK}`,
          "pointer-events": "none",
          "z-index": "9",
          "opacity": "0.85",
          "transition": "transform 140ms ease",
        });

        // Determine “open/closed” state by checking the next content container visibility.
        // Globo implementations vary; this is a robust heuristic:
        const getIsOpen = () => {
          const block = h.closest(".gf-option-block");
          if (!block) return true;

          // If a content box exists and is display:none, consider closed
          const content =
            block.querySelector(".gf-option-box") ||
            block.querySelector(".gf-block-content") ||
            block.querySelector("ul.gf-option-box") ||
            block.querySelector(".gf-range-inputs");

          if (!content) return true;

          const cs = window.getComputedStyle(content);
          return cs.display !== "none" && cs.visibility !== "hidden" && cs.opacity !== "0";
        };

        // Set initial state
        setCaretState(caret, getIsOpen());

        // Bind click once to animate after Globo toggles visibility
        if (!h.dataset.novHeaderCaretBound) {
          h.dataset.novHeaderCaretBound = "1";

          const update = () => setCaretState(caret, getIsOpen());

          h.addEventListener("click", () => {
            setTimeout(update, 0);
            setTimeout(update, 50);
            setTimeout(update, 150);
          });

          h.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
              setTimeout(update, 0);
              setTimeout(update, 50);
              setTimeout(update, 150);
            }
          });
        }
      });
  }

  /* -----------------------------
     BOOT + OBSERVE (Globo re-renders)
  ----------------------------- */

  function boot() {
    applyFixes();

    // Mutation observer catches re-renders/injections
    const obs = new MutationObserver(() => applyFixes());
    obs.observe(document.body, { childList: true, subtree: true });

    // Timed retries handle cases where Globo updates without DOM additions
    let i = 0;
    const t = setInterval(() => {
      applyFixes();
      i += 1;
      if (i >= 25) clearInterval(t);
    }, 250);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();
